import React from 'react';
import { observer } from 'mobx-react';
import { Surface, Symbols } from 'recharts';

import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import Superscript from '../../../../base/Superscript/Superscript.js';
import Tooltip from '../../../../base/Tooltip/Tooltip.js';

import {
	IDefaultLegendContentConfiguration,
	IDefaultLegendContentProps,
	IDefaultLegendContentState,
} from './models/index.js';
import {
	DEFAULT_LEGEND_CONTENT_CATEGORY_KEY,
	DEFAULT_LEGEND_CONTENT_COMPONENT_KEY,
	DEFAULT_LEGEND_CONTENT_PARENT_COMPONENT_KEY,
} from './DefaultLegendContent.meta.js';

const SIZE = 32;

export class DefaultLegendContent extends ComponentBase<
	IDefaultLegendContentProps,
	IDefaultLegendContentState,
	IDefaultLegendContentConfiguration
> {
	categoryKey = DEFAULT_LEGEND_CONTENT_CATEGORY_KEY;
	componentKey = DEFAULT_LEGEND_CONTENT_COMPONENT_KEY;
	get parentComponentKey(): string | undefined {
		return DEFAULT_LEGEND_CONTENT_PARENT_COMPONENT_KEY;
	}

	constructor(props: IDefaultLegendContentProps) {
		super(props);
	}

	static displayName = 'Legend';

	static defaultProps = {
		iconSize: 14,
		layout: 'horizontal',
		align: 'center',
		verticalAlign: 'middle',
		inactiveColor: '#ccc',
	};

	/**
	 * Render the path of icon
	 * @param {Object} data Data of each legend item
	 * @return {String} Path element
	 */
	renderIcon(data: any) {
		const { inactiveColor } = this.props;
		const halfSize = SIZE / 2;
		const sixthSize = SIZE / 6;
		const thirdSize = SIZE / 3;
		const color = data.inactive ? inactiveColor : data.color;

		if (data.type === 'plainline') {
			return (
				<line
					strokeWidth={4}
					fill="none"
					stroke={color}
					strokeDasharray={data.payload.strokeDasharray}
					x1={0}
					y1={halfSize}
					x2={SIZE}
					y2={halfSize}
					className="recharts-legend-icon"
				/>
			);
		}
		if (data.type === 'line') {
			return (
				<path
					strokeWidth={4}
					fill="none"
					stroke={color}
					d={`M0,${halfSize}h${thirdSize}
			  A${sixthSize},${sixthSize},0,1,1,${2 * thirdSize},${halfSize}
			  H${SIZE}M${2 * thirdSize},${halfSize}
			  A${sixthSize},${sixthSize},0,1,1,${thirdSize},${halfSize}`}
					className="recharts-legend-icon"
				/>
			);
		}
		if (data.type === 'rect') {
			return (
				<path
					stroke="none"
					fill={color}
					d={`M0,${SIZE / 8}h${SIZE}v${(SIZE * 3) / 4}h${-SIZE}z`}
					className="recharts-legend-icon"
				/>
			);
		}

		return <Symbols fill={color} cx={halfSize} cy={halfSize} size={SIZE} sizeType="diameter" type={data.type} />;
	}

	/**
	 * Draw items of legend
	 * @return {ReactElement} Items
	 */
	renderItems() {
		const { payload, iconSize, layout, formatter } = this.props;
		const viewBox = { x: 0, y: 0, width: SIZE, height: SIZE };
		const itemStyle = {
			display: layout === 'horizontal' ? 'inline-block' : 'block',
			marginRight: 10,
		};
		const svgStyle = { display: 'inline-block', verticalAlign: 'middle', marginRight: 4 };

		if (payload) {
			return payload.map((entry, i) => {
				const finalFormatter = entry.formatter || formatter;
				const className = `recharts-legend-item legend-item-${i}${entry.inactive && ' inactive'}`;

				if (entry.type === 'none') {
					return null;
				}

				const superscripts: number[] = [];
				const entryPayload: any = entry.payload;
				if (entryPayload && entryPayload._globalDisclaimers) {
					// Loop through and build up the superscripts from the global disclaiemrs
					for (const globalDisclaimer of entryPayload._globalDisclaimers) {
						for (const disclaimer of globalDisclaimer) {
							superscripts.push(disclaimer.superscript);
						}
					}
				}

				// Create superscript text by joining applicable values
				const superscriptText: string = superscripts.join(', ');
				let superscript;
				if (superscriptText) {
					superscript = <Superscript>{superscriptText}</Superscript>;
				}

				return (
					<li className={className} style={itemStyle} key={`legend-item-${i}`}>
						<Surface width={iconSize || 10} height={iconSize || 10} viewBox={viewBox} style={svgStyle}>
							{this.renderIcon(entry)}
						</Surface>
						<span className="recharts-legend-item-text">
							{finalFormatter ? finalFormatter(entry.value, entry, i) : entry.value}
							{superscript}
							<Tooltip {...(entry.payload && entry.payload.toolTip)}></Tooltip>
						</span>
					</li>
				);
			});
		}
	}

	render() {
		const { payload, layout, align } = this.props;

		if (!payload || !payload.length) {
			return null;
		}

		const finalStyle = {
			padding: 0,
			margin: 0,
			textAlign: layout === 'horizontal' ? align : 'left',
		};

		return (
			<ul
				className={`recharts-default-legend ${this.props.className}`}
				style={finalStyle}
				data-selector={this.props['data-selector']}
				aria-hidden={true}
			>
				{this.renderItems()}
			</ul>
		);
	}
}

export default InjectedStyledComponent(
	observer(DefaultLegendContent),
	DEFAULT_LEGEND_CONTENT_CATEGORY_KEY,
	DEFAULT_LEGEND_CONTENT_PARENT_COMPONENT_KEY,
	[DEFAULT_LEGEND_CONTENT_COMPONENT_KEY],
);
